// components/Navbar.js
//
// MOBILE: On screens under 768px wide, the nav links are hidden
// and replaced with a hamburger button (☰). Clicking it toggles
// a full-screen mobile menu overlay.
//
// CONCEPT: useState tracks whether the mobile menu is open.
// A CSS media query (via inline style + window.innerWidth check)
// hides/shows the desktop vs mobile versions.

import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Track whether mobile menu is open
  const [menuOpen, setMenuOpen] = useState(false);

  // Track screen width so we can show/hide correct nav version
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Always start transparent. Goes dark only when user scrolls past 80px.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset to transparent on every page change
  useEffect(() => {
    window.scrollTo(0, 0);
    setScrolled(false);
  }, [location.pathname]);

  // Listen for window resize — update isMobile when screen size changes
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu whenever the route changes (user tapped a link)
  useEffect(() => {
    setMenuOpen(false);
    document.body.style.overflow = '';
  }, [location]);

  const toggleMenu = () => {
    const next = !menuOpen;
    setMenuOpen(next);
    // Prevent body scroll when menu is open
    document.body.style.overflow = next ? 'hidden' : '';
  };

  const links = [
    { to: '/about',        label: 'About' },
    { to: '/process',      label: 'Process' },
    { to: '/services',     label: 'Services' },
    { to: '/gallery',      label: 'Gallery' },
    { to: '/testimonials', label: 'Testimonials' },
    { to: '/contact',      label: 'Contact' },
  ];

const linkStyle = (isActive) => ({
  fontFamily: 'inherit',
  fontSize: '1.1rem',
  letterSpacing: '0.025em',
  color: isActive ? '#F58220' : 'rgba(255,255,255,0.85)',
  transition: 'color 0.2s',
  textDecoration: 'none',
  });

  return (
    <>
      {/* Orange top bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1001,
        background: 'var(--orange)',
        height: '4px',
      }} />

      <nav style={{
        ...s.nav,
        top: '4px', // sit just below the orange bar
        background: scrolled ? 'rgba(14,14,14,0.97)' : 'transparent',
        borderBottom: scrolled ? '1px solid var(--border)' : 'none',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
      }}>
        {/* LOGO */}
        <div style={s.logo} onClick={() => navigate('/')}>
          <img
            src="/images/Urban-Logo.png"
            alt="Urban Kitchens"
            style={{ height: '130px', width: 'auto', display: 'block' }}
          />
        </div>

        {/* DESKTOP NAV LINKS — hidden on mobile */}
        {!isMobile && (
          <div style={s.links}>
            {links.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                style={({ isActive }) => linkStyle(isActive)}
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        )}

        {/* RIGHT SIDE */}
        <div style={s.right}>
          {/* Phone number — hide on small phones */}
          {!isMobile && (
            <span style={s.phone}>📞 02890 132146</span>
          )}

          {/* Book button — always visible */}
          <button style={s.bookBtn} onClick={() => navigate('/contact')}>
            {isMobile ? 'Book' : 'Book\nConsultation'}
          </button>

          {/* Hamburger button — only on mobile */}
          {isMobile && (
            <button style={s.hamburger} onClick={toggleMenu} aria-label="Menu">
              {menuOpen ? '✕' : '☰'}
            </button>
          )}
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY */}
      {isMobile && menuOpen && (
        <div style={s.mobileOverlay}>
          <nav style={s.mobileNav}>
            {links.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                style={({ isActive }) => ({
                  ...s.mobileLink,
                  color: isActive ? 'var(--orange)' : 'var(--text)',
                })}
              >
                {link.label}
              </NavLink>
            ))}

            {/* Phone and email in mobile menu */}
            <div style={s.mobileDivider} />
            <a href="tel:02890132146" style={s.mobileContact}>📞 02890 132146</a>
            <a href="mailto:hello@urbankdcni.co.uk" style={s.mobileContact}>✉ hello@urbankdcni.co.uk</a>
          </nav>
        </div>
      )}
    </>
  );
}

const s = {
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 2rem', height: '150px',
    transition: 'background 0.4s, border-bottom 0.4s',
  },
  logo: { display: 'flex', alignItems: 'center', cursor: 'pointer' },
  logoU: { fontFamily: "'Cormorant Garamond', serif", fontSize: '3rem', fontWeight: 400, color: 'var(--orange)', lineHeight: 1 },
  logoText: { display: 'flex', flexDirection: 'column' },
  logoRban: { fontSize: '1.4rem', color: 'var(--text)', fontFamily: "'Manrope', sans-serif", fontWeight: 400, marginTop: '-4px' },
  logoKitchens: { fontSize: '0.58rem', letterSpacing: '0.3em', color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'Manrope', sans-serif" },
  links: { display: 'flex', alignItems: 'center', gap: '2rem' },
  link: { fontSize: '0.92rem', fontWeight: 400, letterSpacing: '0.04em', transition: 'color 0.2s', textDecoration: 'none' },
  right: { display: 'flex', alignItems: 'center', gap: '1rem' },
  phone: {
  fontSize: '1.1rem',
  fontWeight: 500,
  color: 'rgba(255,255,255,0.85)',
  whiteSpace: 'nowrap',
},
  bookBtn: {
    background: '#F58220',
    color: '#000',
    fontFamily: 'inherit',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    padding: '0 1.5rem',
    height: 40,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
  },
  hamburger: {
    background: 'var(--orange)',
    border: 'none',
    color: '#000',
    fontSize: '1.2rem',
    fontWeight: 900,
    width: 40, height: 40,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },

  // Mobile full-screen overlay
  mobileOverlay: {
    position: 'fixed', inset: 0, top: '150px', zIndex: 999,
    background: 'rgba(14,14,14,0.98)', backdropFilter: 'blur(12px)',
    overflowY: 'auto',
  },
  mobileNav: {
    display: 'flex', flexDirection: 'column',
    padding: '1.5rem 2rem',
  },
  mobileLink: {
    fontFamily: "'Jost', sans-serif",
    fontSize: '1.1rem', fontWeight: 400,
    padding: '0.85rem 0',
    borderBottom: '1px solid var(--border)',
    textDecoration: 'none', transition: 'color 0.2s',
    display: 'block', letterSpacing: '0.05em',
  },
  mobileDivider: { margin: '1.5rem 0', borderTop: '1px solid var(--border)' },
  mobileContact: {
    color: 'var(--text-muted)', fontSize: '0.9rem',
    padding: '0.5rem 0', display: 'block', textDecoration: 'none',
  },
};
